import { useState, useRef, useEffect } from 'react';
import { 
  X, Upload, AlertCircle, FileText, Image as ImageIcon, 
  Video, File, CheckCircle, Loader, HelpCircle 
} from 'lucide-react';
import { helpRequestService } from '../services/helpRequestService';
import { useAuthStore } from '../store/authStore';

const RaiseHelpRequestModal = ({ isOpen, onClose }) => {
  const { user } = useAuthStore();
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    module: '',
    help_type: '',
    severity: '',
    description: '',
    application_id: '',
    is_recurring: false
  });

  const [isApplicationRelated, setIsApplicationRelated] = useState(false);
  const [previousReqId, setPreviousReqId] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showAttachments, setShowAttachments] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const modules = [
    'Applications',
    'Payments',
    'KYC',
    'Disbursement',
    'Account',
    'Other'
  ];

  const helpTypes = [
    { value: 'issue', label: 'Issue', icon: AlertCircle, color: 'rose' },
    { value: 'service_request', label: 'Service Request', icon: HelpCircle, color: 'blue' }
  ];

  const severityLevels = [
    { value: 'low', label: 'Low', color: 'green' },
    { value: 'medium', label: 'Medium', color: 'yellow' },
    { value: 'high', label: 'High', color: 'orange' },
    { value: 'critical', label: 'Critical', color: 'rose' }
  ];

  useEffect(() => {
    if (!isOpen) {
      handleClose();
    }
  }, [isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const newFiles = files.map(file => ({
      file,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      name: file.name,
      size: file.size,
      type: file.type
    }));
    setSelectedFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => {
      const file = prev[index];
      if (file.preview) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const getFileIcon = (type) => {
    if (type.startsWith('image/')) return ImageIcon;
    if (type.startsWith('video/')) return Video;
    return FileText;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.module) newErrors.module = 'Please select a module';
    if (!formData.help_type) newErrors.help_type = 'Please select help type';
    if (!formData.severity) newErrors.severity = 'Please select severity level';
    if (!formData.description.trim()) newErrors.description = 'Please provide a description';
    if (isApplicationRelated && !formData.application_id.trim()) {
      newErrors.application_id = 'Please provide application number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const requestData = { ...formData };
      
      // Only include application_id if checkbox is checked
      if (!isApplicationRelated) {
        delete requestData.application_id;
      }
      
      // Parse comma-separated previous request IDs
      if (formData.is_recurring && previousReqId.trim()) {
        requestData.previous_req_ids = previousReqId
          .split(',')
          .map(id => id.trim())
          .filter(id => id.length > 0);
      }

      // Convert is_recurring to 1/0 for Laravel boolean validation
      requestData.is_recurring = requestData.is_recurring ? 1 : 0;

      // If we have files, create FormData with only the files
      // The service will append the data
      let formDataToSend = null;
      if (selectedFiles.length > 0) {
        formDataToSend = new FormData();
        selectedFiles.forEach((fileObj) => {
          formDataToSend.append('attachments[]', fileObj.file);
        });
      }

      await helpRequestService.createHelpRequest(requestData, formDataToSend);
      
      setShowSuccess(true);
      setTimeout(() => {
        handleClose();
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error submitting help request:', error);
      setErrors({ submit: error.response?.data?.message || 'Failed to submit help request. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      module: '',
      help_type: '',
      severity: '',
      description: '',
      application_id: '',
      is_recurring: false
    });
    setIsApplicationRelated(false);
    setPreviousReqId('');
    selectedFiles.forEach(file => {
      if (file.preview) URL.revokeObjectURL(file.preview);
    });
    setSelectedFiles([]);
    setShowAttachments(false);
    setIsSubmitting(false);
    setShowSuccess(false);
    setErrors({});
  };

  if (!isOpen) return null;

  if (showSuccess) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-md w-full shadow-2xl text-center">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Request Submitted!
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            We'll get back to you soon with updates
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-t-3xl sm:rounded-2xl w-full sm:max-w-2xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Raise Help Request
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Tell us about your issue and we'll help you resolve it
            </p>
          </div>
          <button
            onClick={() => {
              handleClose();
              onClose();
            }}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
          >
            <X className="w-6 h-6 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Form Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Module Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Module <span className="text-rose-500">*</span>
            </label>
            <select
              value={formData.module}
              onChange={(e) => handleInputChange('module', e.target.value)}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-xl focus:border-rose-500 dark:focus:border-rose-500 focus:bg-white dark:focus:bg-gray-600 focus:outline-none transition-all text-gray-900 dark:text-gray-100 font-medium"
            >
              <option value="">Select a module</option>
              {modules.map((module) => (
                <option key={module} value={module}>
                  {module}
                </option>
              ))}
            </select>
            {errors.module && <p className="text-rose-500 text-sm mt-2">{errors.module}</p>}
          </div>

          {/* Help Type */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Request Type <span className="text-rose-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              {helpTypes.map(({ value, label, icon: Icon, color }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => handleInputChange('help_type', value)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    formData.help_type === value
                      ? `bg-${color}-50 dark:bg-${color}-900/20 border-${color}-500 text-${color}-700 dark:text-${color}-400`
                      : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-6 h-6 mx-auto mb-2" />
                  <span className="text-sm font-medium">{label}</span>
                </button>
              ))}
            </div>
            {errors.help_type && <p className="text-rose-500 text-sm mt-2">{errors.help_type}</p>}
          </div>

          {/* Severity */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Severity Level <span className="text-rose-500">*</span>
            </label>
            <select
              value={formData.severity}
              onChange={(e) => handleInputChange('severity', e.target.value)}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-xl focus:border-rose-500 dark:focus:border-rose-500 focus:bg-white dark:focus:bg-gray-600 focus:outline-none transition-all text-gray-900 dark:text-gray-100 font-medium"
            >
              <option value="">Select severity level</option>
              {severityLevels.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            {errors.severity && <p className="text-rose-500 text-sm mt-2">{errors.severity}</p>}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Description <span className="text-rose-500">*</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={4}
              placeholder="Describe your issue or request in detail..."
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-xl focus:border-rose-500 dark:focus:border-rose-500 focus:bg-white dark:focus:bg-gray-600 focus:outline-none transition-all resize-none text-gray-900 dark:text-gray-100 placeholder-gray-400"
            />
            {errors.description && <p className="text-rose-500 text-sm mt-2">{errors.description}</p>}
          </div>

          {/* Application Related Checkbox */}
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-200 dark:border-purple-800">
              <input
                type="checkbox"
                id="is_application_related"
                checked={isApplicationRelated}
                onChange={(e) => setIsApplicationRelated(e.target.checked)}
                className="w-5 h-5 text-purple-500 rounded focus:ring-purple-500"
              />
              <label htmlFor="is_application_related" className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                This issue is related to a specific application
              </label>
            </div>

            {/* Application ID Input (conditional) */}
            {isApplicationRelated && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Application Number <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.application_id}
                  onChange={(e) => handleInputChange('application_id', e.target.value)}
                  placeholder="Enter application number (e.g., APP-2026-001)"
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-xl focus:border-rose-500 dark:focus:border-rose-500 focus:bg-white dark:focus:bg-gray-600 focus:outline-none transition-all text-gray-900 dark:text-gray-100 placeholder-gray-400"
                />
                {errors.application_id && <p className="text-rose-500 text-sm mt-2">{errors.application_id}</p>}
              </div>
            )}
          </div>

          {/* Recurring Issue Checkbox */}
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
              <input
                type="checkbox"
                id="is_recurring"
                checked={formData.is_recurring}
                onChange={(e) => handleInputChange('is_recurring', e.target.checked)}
                className="w-5 h-5 text-blue-500 rounded focus:ring-blue-500"
              />
              <label htmlFor="is_recurring" className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                This is a recurring issue (I've reported this before)
              </label>
            </div>

            {/* Previous Request IDs Input (conditional) */}
            {formData.is_recurring && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Previous Request ID(s)
                </label>
                <input
                  type="text"
                  value={previousReqId}
                  onChange={(e) => setPreviousReqId(e.target.value)}
                  placeholder="Enter previous request IDs (comma-separated, e.g., REQ-2026-001, REQ-2026-002)"
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-xl focus:border-rose-500 dark:focus:border-rose-500 focus:bg-white dark:focus:bg-gray-600 focus:outline-none transition-all text-gray-900 dark:text-gray-100 placeholder-gray-400"
                />
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  💡 Enter multiple request IDs separated by commas if you've reported this issue multiple times
                </p>
              </div>
            )}
          </div>

          {/* Attachments Section Toggle */}
          <div>
            <button
              type="button"
              onClick={() => setShowAttachments(!showAttachments)}
              className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-xl border border-indigo-200 dark:border-indigo-800 hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-3">
                <Upload className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  Attachments {selectedFiles.length > 0 && `(${selectedFiles.length})`}
                </span>
              </div>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {showAttachments ? 'Hide' : 'Show'}
              </span>
            </button>

            {showAttachments && (
              <div className="mt-4 space-y-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,video/*,.pdf,.doc,.docx"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full p-6 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl hover:border-rose-500 dark:hover:border-rose-500 transition-all bg-gray-50/50 dark:bg-gray-700/50"
                >
                  <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Click to upload files
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Images, Videos, PDFs, Documents
                  </p>
                </button>

                {selectedFiles.length > 0 && (
                  <div className="grid grid-cols-2 gap-3">
                    {selectedFiles.map((fileObj, index) => {
                      const FileIcon = getFileIcon(fileObj.type);
                      return (
                        <div
                          key={index}
                          className="relative p-3 bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600"
                        >
                          {fileObj.preview ? (
                            <img
                              src={fileObj.preview}
                              alt={fileObj.name}
                              className="w-full h-24 object-cover rounded-lg mb-2"
                            />
                          ) : (
                            <div className="w-full h-24 bg-gray-100 dark:bg-gray-600 rounded-lg mb-2 flex items-center justify-center">
                              <FileIcon className="w-8 h-8 text-gray-400" />
                            </div>
                          )}
                          <p className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                            {fileObj.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {formatFileSize(fileObj.size)}
                          </p>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="absolute top-1 right-1 p-1 bg-rose-500 hover:bg-rose-600 rounded-full shadow-lg transition-colors"
                          >
                            <X className="w-4 h-4 text-white" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {errors.submit && (
            <div className="p-4 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-xl">
              <p className="text-sm text-rose-600 dark:text-rose-400">{errors.submit}</p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex gap-3">
          <button
            type="button"
            onClick={() => {
              handleClose();
              onClose();
            }}
            disabled={isSubmitting}
            className="flex-1 px-6 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-semibold hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-rose-500 to-pink-600 text-white rounded-xl font-semibold hover:from-rose-600 hover:to-pink-700 transition-all shadow-lg shadow-rose-500/30 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Request'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RaiseHelpRequestModal;
